import React, { createContext, useContext, useEffect, useState } from "react";
import APILoginService from "../rest/login";
import ClientService from "../rest/clientService";
import { isSessionValid, getToken, setClientInfo } from "./sessionManager";
import { logout } from "./authService";
import { usePermissionUpdates } from "../ws/useUserPermissionsUpdates";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  const fetchUser = async () => {
    try {
      const userData = await APILoginService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      if (error?.response?.status === 401) {
        logout();
      }
    }
  };

  const getClientData = async () => {
    try {
      const data = await ClientService.getClientInfo();
      setClientInfo(data);
    } catch (err) {
      console.warn("Erro ao buscar clientInfo:", err);
    }
  };

  const login = async (credentials) => {
    const response = await APILoginService.login(credentials);

    if (response) {
      setIsAuthenticated(true);
      setUser(response.user);

      await getClientData();
      return response;
    }

    return null;
  };

  const hasRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const hasPermission = (resource, action) => {
    if (!user || !user.permissions) return false;

    return user.permissions.some(
      (p) => p.resource === resource && p.action === action
    );
  };

  // Assina /topic/permissions-{user.id}: quando um admin altera as permissões deste usuário
  // pela tela de gestão (PermissionsServiceImpl.updateUserPermissions), refaz fetchUser() para
  // que hasPermission() reflita o novo conjunto na hora, sem precisar de logout/login.
  usePermissionUpdates({ user, fetchUser });

  useEffect(() => {

    const initAuth = async () => {

      if (!isSessionValid()) {
        logout();
        setIsLoadingAuth(false);
        return;
      }

      const token = getToken();

      if (token) {
        setIsAuthenticated(true);

        await Promise.allSettled([
          fetchUser(),
          getClientData()
        ]);
      }

      setIsLoadingAuth(false);
    };

    initAuth();

  }, []);

  useEffect(() => {

    const interval = setInterval(() => {

      if (!isSessionValid()) {
        logout();
      }

    }, 60000);

    return () => clearInterval(interval);

  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoadingAuth,
        login,
        logout,
        fetchUser,
        user,
        hasRole,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );

};

export const useAuth = () => useContext(AuthContext);